/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name base
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/complex/float64/base}
*/
import base from './../../float64/base';
setReadOnly( ns, 'base', base );

/**
* @name conj
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/conj}
*/
import conj from './../../float64/conj';
setReadOnly( ns, 'conj', conj );

/**
* @name Complex128
* @memberof ns
* @readonly
* @constructor
* @see {@link module:@stdlib/complex/float64/ctor}
*/
import Complex128 from './../../float64/ctor';
setReadOnly( ns, 'Complex128', Complex128 );

/**
* @name imag
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/imag}
*/
import imag from './../../float64/imag';
setReadOnly( ns, 'imag', imag );

/**
* @name parseComplex128
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/parse}
*/
import parseComplex128 from './../../float64/parse';
setReadOnly( ns, 'parseComplex128', parseComplex128 );

/**
* @name real
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/real}
*/
import real from './../../float64/real';
setReadOnly( ns, 'real', real );

/**
* @name reim
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/reim}
*/
import reim from './../../float64/reim';
setReadOnly( ns, 'reim', reim );

/**
* @name reviveComplex128
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/reviver}
*/
import reviveComplex128 from './../../float64/reviver';
setReadOnly( ns, 'reviveComplex128', reviveComplex128 );


// EXPORTS //

export default ns;
